import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Suspense, lazy } from "react";
import AnalyticsListener from "@/components/analytics/AnalyticsListener";
import EventTracker from "@/components/analytics/EventTracker";

// Eager: Home is the main entry point; lazy-loading it delayed its LCP text.
import Home from "@/pages/Home";

// Lazy: everything else. These render only on their route, so we don't
// need to ship them in the initial bundle served to a home-page visitor.
const ServicesIndex = lazy(() => import("@/pages/ServicesIndex"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminSetup = lazy(() => import("@/pages/AdminSetup"));

// Helpers to pluck named exports out of a lazy-loaded module. Keeps the
// per-page usage identical while sharing a single chunk per grouped file.
const lazyNamed = (importPromise, exportName) =>
  lazy(() => importPromise().then((m) => ({ default: m[exportName] })));

// ServicePages1 exports — code-split off the homepage bundle so the initial
// Home visitor doesn't pay for Implanturi/AllOn4/Cerec/Radiologie parse cost.
const Implanturi = lazyNamed(() => import("@/pages/ImplantPage"), "Implanturi");
const AllOn4     = lazyNamed(() => import("@/pages/ServicePages1"), "AllOn4");
const Cerec      = lazyNamed(() => import("@/pages/ServicePages1"), "Cerec");
const Radiologie = lazyNamed(() => import("@/pages/ServicePages1"), "Radiologie");

// ServicePages2 exports
const Endodontie      = lazyNamed(() => import("@/pages/ServicePages2"), "Endodontie");
const Parodontologie  = lazyNamed(() => import("@/pages/ServicePages2"), "Parodontologie");
const Igienizare      = lazyNamed(() => import("@/pages/ServicePages2"), "Igienizare");
const Ortodontie      = lazyNamed(() => import("@/pages/ServicePages2"), "Ortodontie");
const Urgente         = lazyNamed(() => import("@/pages/ServicePages2"), "Urgente");
const Protetica       = lazyNamed(() => import("@/pages/ServicePages2"), "Protetica");
// ServicePages3
const Modjaw            = lazyNamed(() => import("@/pages/ServicePages3"), "Modjaw");
const DiagnosticDigital = lazyNamed(() => import("@/pages/ServicePages3"), "DiagnosticDigital");
const AlbireBiolase     = lazyNamed(() => import("@/pages/ServicePages3"), "AlbireBiolase");
const BruxismUzura      = lazyNamed(() => import("@/pages/ServicePages3"), "BruxismUzura");
const HerpesLabial      = lazyNamed(() => import("@/pages/HerpesPage"), "HerpesLabial");
// Legacy MegaGen informational URL (kept live because it still has GSC
// visibility for MegaGen-specific queries; historic content was retired).
const MegaGenPage       = lazyNamed(() => import("@/pages/MegaGenPage"), "MegaGenPage");
const Pedodontie        = lazyNamed(() => import("@/pages/PediatricPage"), "Pedodontie");
const EsteticaDentara   = lazyNamed(() => import("@/pages/AestheticsPage"), "EsteticaDentara");
const SparkAligners     = lazyNamed(() => import("@/pages/SparkAlignersPage"), "SparkAligners");
// Articles
const ArticlesIndex   = lazyNamed(() => import("@/pages/Articles"), "ArticlesIndex");
const ArticleDetail   = lazyNamed(() => import("@/pages/Articles"), "ArticleDetail");
// InfoPages
const Echipa                = lazyNamed(() => import("@/pages/InfoPages"), "Echipa");
const CazuriTratate         = lazyNamed(() => import("@/pages/InfoPages"), "CazuriTratate");
const PacientiStrainatate   = lazyNamed(() => import("@/pages/InfoPages"), "PacientiStrainatate");
const Tarife                = lazyNamed(() => import("@/pages/InfoPages"), "Tarife");
const Despre                = lazyNamed(() => import("@/pages/InfoPages"), "Despre");
const FAQ                   = lazyNamed(() => import("@/pages/InfoPages"), "FAQ");
const Contact               = lazyNamed(() => import("@/pages/InfoPages"), "Contact");
// Pricing pillar page (own file, own chunk).
const PretImplantDentar     = lazyNamed(() => import("@/pages/PretImplantDentar"), "PretImplantDentar");
// Implantology cluster support page.
const EtapeleImplant        = lazyNamed(() => import("@/pages/EtapeleTratamentuluiImplant"), "EtapeleTratamentuluiImplant");
// LegalPages
const PoliticaConfidentialitate = lazyNamed(() => import("@/pages/LegalPages"), "PoliticaConfidentialitate");
const PoliticaCookies           = lazyNamed(() => import("@/pages/LegalPages"), "PoliticaCookies");
const Termeni                   = lazyNamed(() => import("@/pages/LegalPages"), "Termeni");
const ProiecteEuropene          = lazy(() => import("@/pages/EUProject"));
const Blog                      = lazy(() => import("@/pages/Blog"));
const SoroArticle               = lazy(() => import("@/pages/SoroArticle"));
const NotFound                  = lazy(() => import("@/pages/NotFound"));

// Minimal, visually-neutral fallback so lazy chunks don't cause a flash.
// Matches the site background so it is imperceptible on fast networks.
const RouteFallback = () => (
  <div style={{ minHeight: "60vh", background: "#ffffff" }} aria-hidden="true" />
);

// Redirects the current match to `to`, preserving the incoming query string
// (e.g. UTM tags). `replace` keeps the bad URL out of history so the browser
// back button behaves as expected. String-form <Navigate to="/x" replace />
// alone would drop the query string, hence this tiny wrapper.
function PreserveQueryRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ""}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/servicii" element={<ServicesIndex />} />
            <Route path="/implant-dentar-bacau" element={<Implanturi />} />
            {/* Legacy 404-fixup: some external references (and previously the
                mobile sticky bar/WhatsApp CTAs on other sites) linked to
                /implanturi-dentare, which never existed and returned the SPA
                404. Both the trailing-slash and no-trailing-slash forms now
                redirect to the working implantology page. Query parameters
                are preserved so UTM tags survive the hop. `replace` avoids
                polluting the browser history. */}
            <Route path="/implanturi-dentare" element={<PreserveQueryRedirect to="/implant-dentar-bacau" />} />
            <Route path="/implanturi-dentare/" element={<PreserveQueryRedirect to="/implant-dentar-bacau" />} />
            {/* New canonical URL for the All-on-4 / All-on-6 landing page.
                Both trailing-slash and non-slash variants render the AllOn4
                component so the page is directly reachable at either form. */}
            <Route path="/dinti-ficsi-pe-implanturi-bacau" element={<AllOn4 />} />
            <Route path="/dinti-ficsi-pe-implanturi-bacau/" element={<AllOn4 />} />
            {/* Legacy URL. Kept live so nothing 404s while the real HTTP 308
                redirect is being applied at the Emergent edge (see Support
                ticket referenced in the deploy notes). The AllOn4 component
                emits a canonical pointing to the new URL, so Google
                consolidates ranking signal to /dinti-ficsi-pe-implanturi-bacau/
                immediately, regardless of which URL is fetched. Remove this
                route only after the edge 308 is live. */}
            <Route path="/dinti-ficsi-all-on-4-all-on-6" element={<AllOn4 />} />
            <Route path="/coroane-dentare-cerec" element={<Cerec />} />
            <Route path="/protetica-dentara" element={<Protetica />} />
            <Route path="/radiologie-digitala-cbct" element={<Radiologie />} />
            {/* Legacy Wordpress URL preserved as an alias — declared as
                canonical in Google Search Console. Both the trailing-slash
                and no-trailing-slash forms render the same Radiologie page. */}
            <Route path="/specializari/radiologie-digitala-cbct-axeos" element={<Radiologie />} />
            <Route path="/specializari/radiologie-digitala-cbct-axeos/" element={<Radiologie />} />
            <Route path="/endodontie-microscop" element={<Endodontie />} />
            <Route path="/parodontologie-laser" element={<Parodontologie />} />
            {/* Legacy alias — `/parodontologie` never had a route and returns
                the SPA 404. Some homepage brand-logo links and older external
                references still hit it, so redirect permanently (client-side)
                to the working parodontology page. Query params preserved. */}
            <Route path="/parodontologie" element={<PreserveQueryRedirect to="/parodontologie-laser" />} />
            <Route path="/parodontologie/" element={<PreserveQueryRedirect to="/parodontologie-laser" />} />
            <Route path="/igienizare-dentara-ems" element={<Igienizare />} />
            <Route path="/ortodontie" element={<Ortodontie />} />
            <Route path="/spark-aligners-bacau" element={<SparkAligners />} />
            <Route path="/urgente-stomatologice" element={<Urgente />} />
            <Route path="/stomatologie-copii-bacau" element={<Pedodontie />} />
            <Route path="/estetica-dentara-bacau" element={<EsteticaDentara />} />
            <Route path="/modjaw-4d-bacau" element={<Modjaw />} />
            <Route path="/diagnostic-digital-integrat" element={<DiagnosticDigital />} />
            <Route path="/albire-dentara-laser-biolase" element={<AlbireBiolase />} />
            <Route path="/tratament-bruxism-uzura-dentara" element={<BruxismUzura />} />
            <Route path="/herpes" element={<HerpesLabial />} />
            {/* Historic legacy URL preserved for its Google Search Console
                visibility on MegaGen-specific queries. The old CMS body was
                retired; this route serves modern, conservative replacement
                content at the exact indexed URL. Both trailing-slash and
                non-slash forms render the same component; the page emits
                a canonical WITH trailing slash to match the historic
                indexed form. */}
            <Route path="/implant-dentar-megagen-de-ce-sa-l-alegi" element={<MegaGenPage />} />
            <Route path="/implant-dentar-megagen-de-ce-sa-l-alegi/" element={<MegaGenPage />} />
            {/* /articole legacy CMS index is now empty; live articles are
                published via Soro at /blog. Header, mobile menu and footer
                all point to /blog; this redirect catches any inbound links
                (bookmarks, external backlinks) still hitting /articole.
                `/articole/:slug` deep-links for individual legacy CMS
                articles are preserved on the line below. */}
            <Route path="/articole" element={<PreserveQueryRedirect to="/blog" />} />
            <Route path="/articole/:slug" element={<ArticleDetail />} />
            <Route path="/echipa-medicala" element={<Echipa />} />
            <Route path="/cazuri-tratate" element={<CazuriTratate />} />
            <Route path="/pacienti-din-strainatate" element={<PacientiStrainatate />} />
            <Route path="/tarife" element={<Tarife />} />
            <Route path="/pret-implant-dentar-bacau" element={<PretImplantDentar />} />
            <Route path="/etapele-tratamentului-cu-implant-dentar" element={<EtapeleImplant />} />
            <Route path="/despre-noi" element={<Despre />} />
            <Route path="/intrebari-frecvente" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/politica-confidentialitate" element={<PoliticaConfidentialitate />} />
            {/* Canonical URL used in TikTok / Meta lead forms — MUST remain accessible. */}
            <Route path="/politica-de-confidentialitate" element={<PoliticaConfidentialitate />} />
            <Route path="/politica-cookies" element={<PoliticaCookies />} />
            <Route path="/termeni-conditii" element={<Termeni />} />
            <Route path="/proiecte-europene" element={<ProiecteEuropene />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<SoroArticle />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/setup" element={<AdminSetup />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {/*
          Placed AFTER <Suspense> so its useEffect fires after each page's
          PageLayout has updated document.title. This guarantees GA4 receives
          the correct page_title for the newly-rendered route.
        */}
        <AnalyticsListener />
        <EventTracker />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
